import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import {
  Shield, Plus, Trash2, CheckCircle2, AlertCircle, Loader2, Sparkles, Sliders, Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PRESET_SUM_INSURED = ["3,00,000", "5,00,000", "7,50,000", "10,00,000", "15,00,000", "20,00,000", "25,00,000"];
const PRESET_TOP_UP = ["1,00,000", "2,00,000", "5,00,000", "10,00,000"];

export default function HealthInsuranceMaster() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newSlabInput, setNewSlabInput] = useState('');
  const [newTopUpInput, setNewTopUpInput] = useState('');

  const [policy, setPolicy] = useState({
    policy_name: 'Group Mediclaim Policy',
    policy_type: 'Self + Family',
    sum_insured_options: ["3,00,000", "5,00,000", "7,50,000", "10,00,000", "15,00,000"],
    default_sum_insured: "5,00,000",
    employee_contribution: 0,
    employer_contribution: 100,
    top_up_allowed: true,
    top_up_slabs: ["1,00,000", "2,00,000", "5,00,000"],
    parent_cover_allowed: true,
    effective_from: '2026-01-01',
    effective_to: '2026-12-31',
    is_active: true,
  });

  const loadPolicy = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/health-insurance-policy`, { withCredentials: true });
      if (res.data) {
        setPolicy({
          policy_name: res.data.policy_name || 'Group Mediclaim Policy',
          policy_type: res.data.policy_type || 'Self + Family',
          sum_insured_options: Array.isArray(res.data.sum_insured_options) ? res.data.sum_insured_options : ["3,00,000", "5,00,000"],
          default_sum_insured: res.data.default_sum_insured || "5,00,000",
          employee_contribution: res.data.employee_contribution ?? 0,
          employer_contribution: res.data.employer_contribution ?? 100,
          top_up_allowed: res.data.top_up_allowed ?? true,
          top_up_slabs: Array.isArray(res.data.top_up_slabs) ? res.data.top_up_slabs : ["1,00,000", "2,00,000"],
          parent_cover_allowed: res.data.parent_cover_allowed ?? true,
          effective_from: res.data.effective_from || '2026-01-01',
          effective_to: res.data.effective_to || '2026-12-31',
          is_active: res.data.is_active ?? true,
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load Health Insurance policy master');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPolicy(); }, [loadPolicy]);

  const addSlab = (amount) => {
    const val = String(amount || '').trim();
    if (!val) return;
    if (policy.sum_insured_options.includes(val)) {
      toast.error(`Slab '₹${val}' is already added.`);
      return;
    }
    setPolicy(prev => {
      const updated = [...prev.sum_insured_options, val];
      return {
        ...prev,
        sum_insured_options: updated,
        default_sum_insured: prev.default_sum_insured || val,
      };
    });
    setNewSlabInput('');
  };

  const removeSlab = (val) => {
    if (policy.sum_insured_options.length <= 1) {
      toast.error('You must keep at least one Sum Insured coverage option.');
      return;
    }
    setPolicy(prev => {
      const updated = prev.sum_insured_options.filter(s => s !== val);
      const newDef = updated.includes(prev.default_sum_insured) ? prev.default_sum_insured : updated[0];
      return {
        ...prev,
        sum_insured_options: updated,
        default_sum_insured: newDef,
      };
    });
  };

  const addTopUpSlab = (amount) => {
    const val = String(amount || '').trim();
    if (!val) return;
    if (policy.top_up_slabs.includes(val)) {
      toast.error(`Top-up slab '₹${val}' is already added.`);
      return;
    }
    setPolicy(prev => ({
      ...prev,
      top_up_slabs: [...prev.top_up_slabs, val],
    }));
    setNewTopUpInput('');
  };

  const removeTopUpSlab = (val) => {
    setPolicy(prev => ({
      ...prev,
      top_up_slabs: prev.top_up_slabs.filter(s => s !== val),
    }));
  };

  const handleSave = async () => {
    if (!policy.policy_name.trim()) {
      toast.error('Policy Name is required.');
      return;
    }
    if (!policy.sum_insured_options || policy.sum_insured_options.length === 0) {
      toast.error('At least one Sum Insured option must be configured.');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/health-insurance-policy`, policy, { withCredentials: true });
      toast.success('Health Insurance Master policy updated successfully! Employees will see updated Sum Insured options.');
      loadPolicy();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save Health Insurance policy configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="health-insurance-master-page">
        {/* Top Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 p-6 rounded-2xl text-white shadow-lg">
          <div>
            <div className="flex items-center gap-2">
              <span className="bg-blue-500/30 text-blue-200 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border border-blue-400/20">Company Policy Master</span>
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${policy.is_active ? 'bg-emerald-500/30 text-emerald-200 border-emerald-400/30' : 'bg-slate-500/30 text-slate-300 border-slate-400/30'}`}>
                {policy.is_active ? 'Active Policy' : 'Inactive'}
              </span>
            </div>
            <h1 className="text-2xl font-bold tracking-tight mt-2 flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-400" /> Health Insurance Master Configuration
            </h1>
            <p className="text-xs text-blue-200 mt-1 max-w-2xl">
              Configure company health insurance policy rules, dynamic sum insured coverage slabs, contribution ratios, and top-up elections for your employees.
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            data-testid="btn-save-health-master"
            className="bg-[#0052FF] hover:bg-[#0043D1] text-white font-semibold flex items-center gap-2 px-5 py-2.5 shadow-md shrink-0"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save Policy Setup
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 bg-white border border-slate-200 rounded-xl">
            <Loader2 className="w-8 h-8 animate-spin text-[#0052FF]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel: Policy Configuration Forms */}
            <div className="lg:col-span-2 space-y-6">
              {/* Card 1: Basic Policy Information */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-2 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[#0052FF]" /> 1. Policy Identification &amp; Type
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">Policy Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={policy.policy_name}
                      onChange={e => setPolicy({ ...policy, policy_name: e.target.value })}
                      placeholder="e.g. Group Mediclaim GMC Policy 2026"
                      className="w-full border border-slate-300 rounded px-3 py-2 text-xs font-semibold text-slate-800"
                      data-testid="input-policy-name"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">Default Policy Coverage Scope</label>
                    <select
                      value={policy.policy_type}
                      onChange={e => setPolicy({ ...policy, policy_type: e.target.value })}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-xs font-medium text-slate-800 bg-white"
                      data-testid="select-default-policy-type"
                    >
                      <option value="Self">Self Only</option>
                      <option value="Self + Spouse">Self + Spouse</option>
                      <option value="Self + Family">Self + Family (Self + Spouse + Children)</option>
                      <option value="Self + Family + Parents">Self + Family + Parents / In-laws</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Card 2: Company Configured Sum Insured Options (KEY REQUIREMENT) */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4" data-testid="sum-insured-config-card">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                      <Sliders className="w-4 h-4 text-[#0052FF]" /> 2. Company Configured Sum Insured Options (Coverage Slabs)
                    </h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Define the exact coverage amounts available to employees. Employees will only see these options in their portal dropdown.
                    </p>
                  </div>
                  <span className="text-[10px] bg-blue-100 text-[#0052FF] font-bold px-2.5 py-1 rounded-full border border-blue-200">
                    {policy.sum_insured_options.length} Slabs Active
                  </span>
                </div>

                {/* Preset quick buttons */}
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1.5">Quick Add Presets:</label>
                  <div className="flex flex-wrap gap-1.5">
                    {PRESET_SUM_INSURED.map(amt => (
                      <button
                        key={amt}
                        type="button"
                        onClick={() => addSlab(amt)}
                        disabled={policy.sum_insured_options.includes(amt)}
                        className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors flex items-center gap-1 ${
                          policy.sum_insured_options.includes(amt)
                            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                            : 'bg-blue-50 text-[#0052FF] border-blue-200 hover:bg-blue-100'
                        }`}
                      >
                        <Plus className="w-3 h-3" /> ₹{amt}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom slab add input */}
                <div className="flex items-center gap-2 pt-1">
                  <input
                    type="text"
                    value={newSlabInput}
                    onChange={e => setNewSlabInput(e.target.value)}
                    placeholder="Enter custom slab (e.g. 12,50,000)"
                    className="border border-slate-300 rounded px-3 py-1.5 text-xs w-60"
                    data-testid="input-custom-sum-insured"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => addSlab(newSlabInput)}
                    className="bg-slate-800 hover:bg-slate-900 text-white text-xs"
                    data-testid="btn-add-custom-slab"
                  >
                    Add Slab
                  </Button>
                </div>

                {/* Active Slabs Tags List */}
                <div className="pt-2">
                  <label className="block text-[11px] font-semibold text-slate-700 mb-2">Configured Dropdown Options:</label>
                  <div className="flex flex-wrap gap-2">
                    {policy.sum_insured_options.map((slab) => (
                      <div
                        key={slab}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-bold shadow-sm transition-all ${
                          slab === policy.default_sum_insured
                            ? 'bg-blue-600 text-white border-blue-700 ring-2 ring-blue-300'
                            : 'bg-slate-50 text-slate-800 border-slate-200 hover:bg-slate-100'
                        }`}
                        data-testid={`slab-tag-${slab}`}
                      >
                        <span>₹{slab}</span>
                        {slab === policy.default_sum_insured && (
                          <span className="text-[9px] uppercase bg-white/20 px-1.5 py-0.5 rounded font-extrabold">Default</span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeSlab(slab)}
                          className="text-slate-400 hover:text-red-600 hover:bg-red-50 p-0.5 rounded transition-colors"
                          title="Remove slab"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Default Coverage Select */}
                <div className="pt-2 border-t border-slate-100">
                  <label className="block font-semibold text-slate-700 mb-1">Company Default Sum Insured Slab</label>
                  <select
                    value={policy.default_sum_insured}
                    onChange={e => setPolicy({ ...policy, default_sum_insured: e.target.value })}
                    className="w-full sm:w-72 border border-slate-300 rounded px-3 py-2 text-xs font-bold text-[#0052FF] bg-blue-50/50"
                    data-testid="select-master-default-sum-insured"
                  >
                    {policy.sum_insured_options.map(slab => (
                      <option key={slab} value={slab}>₹{slab} (Default Selection)</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1">This slab will be automatically pre-selected when an employee opens the portal.</p>
                </div>
              </div>

              {/* Card 3: Contribution Ratios & Top-Up Rules */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-2">
                  3. Contributions, Top-Up &amp; Parental Cover Policy Rules
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">Employer Contribution (%)</label>
                    <input
                      type="number"
                      value={policy.employer_contribution}
                      onChange={e => setPolicy({ ...policy, employer_contribution: parseFloat(e.target.value) || 0 })}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-xs font-medium"
                      data-testid="input-employer-contrib"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">Employee Contribution (%)</label>
                    <input
                      type="number"
                      value={policy.employee_contribution}
                      onChange={e => setPolicy({ ...policy, employee_contribution: parseFloat(e.target.value) || 0 })}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-xs font-medium"
                      data-testid="input-employee-contrib"
                    />
                  </div>
                </div>

                {/* Top-up Allowed Toggle */}
                <div className="pt-2 border-t border-slate-100 space-y-3 text-xs">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-bold text-slate-800">Top-Up Coverage Allowed?</span>
                      <p className="text-[11px] text-slate-500">Allow employees to elect optional top-up insurance coverage.</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={policy.top_up_allowed}
                        onChange={e => setPolicy({ ...policy, top_up_allowed: e.target.checked })}
                        className="sr-only peer"
                        data-testid="toggle-top-up-allowed"
                      />
                      <div className="w-9 h-5 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#0052FF]"></div>
                    </label>
                  </div>

                  {policy.top_up_allowed && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
                      <label className="block font-semibold text-slate-700">Top-Up Coverage Slabs:</label>
                      <div className="flex flex-wrap gap-1.5">
                        {policy.top_up_slabs.map(slab => (
                          <span key={slab} className="bg-white border border-slate-200 text-slate-800 text-[11px] font-semibold px-2.5 py-1 rounded-md flex items-center gap-1">
                            ₹{slab}
                            <button type="button" onClick={() => removeTopUpSlab(slab)} className="text-slate-400 hover:text-red-600">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 pt-1">
                        <input
                          type="text"
                          value={newTopUpInput}
                          onChange={e => setNewTopUpInput(e.target.value)}
                          placeholder="e.g. 5,00,000"
                          className="border border-slate-300 rounded px-2.5 py-1 text-xs w-44 bg-white"
                        />
                        <Button type="button" size="sm" variant="outline" onClick={() => addTopUpSlab(newTopUpInput)}>Add Top-Up</Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Parent Cover Allowed Toggle */}
                <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
                  <div>
                    <span className="font-bold text-slate-800">Parental Coverage Allowed?</span>
                    <p className="text-[11px] text-slate-500">Allow employees to include parents / in-laws under policy.</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={policy.parent_cover_allowed}
                      onChange={e => setPolicy({ ...policy, parent_cover_allowed: e.target.checked })}
                      className="sr-only peer"
                      data-testid="toggle-parent-cover-allowed"
                    />
                    <div className="w-9 h-5 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#0052FF]"></div>
                  </label>
                </div>
              </div>

              {/* Card 4: Effective Dates & Status */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#0052FF]" /> 4. Policy Validity &amp; Active Status
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">Effective From</label>
                    <input
                      type="date"
                      value={policy.effective_from}
                      onChange={e => setPolicy({ ...policy, effective_from: e.target.value })}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-xs"
                      data-testid="input-effective-from"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">Effective To</label>
                    <input
                      type="date"
                      value={policy.effective_to}
                      onChange={e => setPolicy({ ...policy, effective_to: e.target.value })}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-xs"
                      data-testid="input-effective-to"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">Policy Status</label>
                    <select
                      value={policy.is_active ? 'active' : 'inactive'}
                      onChange={e => setPolicy({ ...policy, is_active: e.target.value === 'active' })}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-xs font-semibold bg-white"
                      data-testid="select-policy-status"
                    >
                      <option value="active">Active Policy</option>
                      <option value="inactive">Inactive Policy</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel: Live Employee Portal Dropdown Preview Box */}
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-slate-900 text-white rounded-xl p-5 shadow-xl space-y-4 sticky top-6">
                <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-blue-400 flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-amber-400" /> Employee Portal Live Preview
                  </h3>
                  <span className="text-[10px] bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded border border-blue-500/30">
                    Real-time Dropdown
                  </span>
                </div>

                <p className="text-[11px] text-slate-300 leading-relaxed">
                  This preview shows the exact <b>Sum Insured Elected</b> control employees will see in their Employee Portal based on your configuration.
                </p>

                {/* Simulated Employee Read-Only Card Box */}
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 space-y-3">
                  <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Simulated Employee View</div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-200 mb-1">Sum Insured Coverage <span className="text-slate-400 font-normal">(Company Policy)</span></label>
                    <div className="w-full border border-slate-700 bg-slate-900 rounded-lg px-3.5 py-2 text-xs font-bold text-blue-400 flex items-center justify-between">
                      <span>₹{policy.default_sum_insured || '5,00,000'}</span>
                      <span className="text-[10px] bg-blue-500/20 text-blue-300 font-bold px-2 py-0.5 rounded border border-blue-500/30 uppercase">
                        Read Only
                      </span>
                    </div>
                  </div>
                  <div className="text-[10px] text-emerald-400 italic bg-emerald-950/40 p-2 rounded border border-emerald-800/40">
                    No dropdown shown to employee. Coverage amount is fixed by company policy.
                  </div>
                </div>

                <div className="space-y-2 text-xs text-slate-300 border-t border-slate-800 pt-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Policy Name:</span>
                    <span className="font-semibold text-white">{policy.policy_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Total Configured Slabs:</span>
                    <span className="font-bold text-blue-400">{policy.sum_insured_options.length} Slabs</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Default Selected Slab:</span>
                    <span className="font-bold text-emerald-400">₹{policy.default_sum_insured}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Top-Up Allowed:</span>
                    <span className="font-semibold">{policy.top_up_allowed ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Parental Cover Allowed:</span>
                    <span className="font-semibold">{policy.parent_cover_allowed ? 'Yes' : 'No'}</span>
                  </div>
                </div>

                <Button
                  onClick={handleSave}
                  disabled={saving || loading}
                  className="w-full bg-[#0052FF] hover:bg-[#0043D1] text-white text-xs font-semibold py-2.5"
                >
                  {saving ? 'Saving Setup...' : 'Publish Policy Setup to Portal'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
